// Patterns tab: the common shapes of this topic's problem the guide reflects
// back once it has explored enough, plus the bridge to an offer and the
// closing lines.

import type { PatternCard, TalkTrackContent } from "../../types/talkTrack";
import { ListField, SelectField, TextField } from "./fields";
import { nextUnitId } from "./format";
import { UnitCard } from "./UnitCard";
import { useUnitContext } from "./unitContext";

export function PatternsEditor({
  topicId,
  content,
  onChange,
}: {
  topicId: string;
  content: TalkTrackContent;
  onChange: (next: TalkTrackContent) => void;
}) {
  const { editable } = useUnitContext();
  const patterns = content.patterns ?? [];
  const setPatterns = (next: PatternCard[]) => onChange({ ...content, patterns: next });
  const patch = (i: number, p: Partial<PatternCard>) =>
    setPatterns(patterns.map((c, j) => (j === i ? { ...c, ...p } : c)));
  const explainerIds = content.explainers.map((e) => e.id);
  const moduleIds = content.modules.map((m) => m.id);

  function add() {
    const id = nextUnitId(content, `${topicId}.pattern.`, patterns.length + 1);
    setPatterns([
      ...patterns,
      { id, name: "", signals: [], reflection: "", explainerId: null, moduleId: null },
    ]);
  }

  return (
    <>
      {patterns.length === 0 && (
        <div className="state-box">No patterns yet{editable ? " — add one below." : "."}</div>
      )}
      {patterns.map((p, i) => (
        <UnitCard key={p.id} unitId={p.id} kind="Pattern" title={p.name}
          onRemove={() => setPatterns(patterns.filter((_, j) => j !== i))}>
          <TextField label="Name" editable={editable} value={p.name} onChange={(name) => patch(i, { name })} />
          <ListField label="Signals (facts or phrases that suggest it)" editable={editable} value={p.signals}
            onChange={(signals) => patch(i, { signals })} />
          <TextField label="Reflection ([placeholders] filled from what the user said; ends in a check)"
            editable={editable} multiline rows={3} value={p.reflection}
            onChange={(reflection) => patch(i, { reflection })} />
          {/* Full width: unit ids are long (anxiety.explain.the_unfinished_script). */}
          <SelectField label="Explainer that helps" editable={editable} value={p.explainerId || ""}
            options={explainerIds} emptyLabel="none"
            onChange={(id) => patch(i, { explainerId: id || null })} />
          <SelectField label="Module that helps" editable={editable} value={p.moduleId || ""}
            options={moduleIds} emptyLabel="none"
            onChange={(id) => patch(i, { moduleId: id || null })} />
        </UnitCard>
      ))}
      {editable && <button className="btn" onClick={add}>+ Pattern</button>}

      <h3>Bridge and closing</h3>
      <TextField label="Bridge from a named pattern to an offer" editable={editable} value={content.bridge ?? ""}
        placeholder="Would it help to understand why this happens, or try one small thing for it together?"
        onChange={(bridge) => onChange({ ...content, bridge })} />
      <ListField label="Closing lines (a one-line summary, then a next step the user chooses)" editable={editable}
        value={content.closings ?? []} onChange={(closings) => onChange({ ...content, closings })} />
    </>
  );
}
